import { type Island } from '@rta/rta-blueprint-library';
import { useCallback, useState } from 'react';

import type { ExtendedPanelRole, PanelThicknessOverrides } from '@rta/rta-blueprint-library';

export type UsePanelThicknessReturn = {
	advancedOpen: boolean;
	uniform: boolean;
	thicknessValues: PanelThicknessOverrides;
	setAdvancedOpen: (open: boolean) => void;
	initFromIsland: (island: Island) => void;
	handleUniformToggle: (checked: boolean) => void;
	handleUniformThicknessChange: (value: number) => void;
	handlePanelThicknessChange: (role: ExtendedPanelRole, value: number) => void;
	handleResetToDefault: () => void;
};

export const usePanelThickness = (island: Island | null): UsePanelThicknessReturn => {
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const [uniform, setUniform] = useState(true);
	const [thicknessValues, setThicknessValues] = useState<PanelThicknessOverrides>({});

	const applyConfig = useCallback((isUniform: boolean, values: PanelThicknessOverrides): void => {
		if (!island) return;

		if (isUniform) {
			const thickness = values.front ?? island.surround?.getThicknessForRole('front') ?? 0.75;
			island.panelThicknessConfig = { uniform: true, thickness };
		} else {
			island.panelThicknessConfig = { uniform: false, panels: { ...values } };
		}
	}, [island]);

	const initFromIsland = useCallback((resolvedIsland: Island): void => {
		const config = resolvedIsland.panelThicknessConfig;
		if (config) {
			setUniform(config.uniform);
			if (config.uniform) {
				setThicknessValues({ front: config.thickness });
			} else {
				setThicknessValues(config.panels ?? {});
			}
			setAdvancedOpen(true);
		} else {
			setUniform(true);
			const defaultThickness = resolvedIsland.getThicknessForRole('front');
			setThicknessValues({ front: defaultThickness });
		}
	}, []);

	const handleUniformThicknessChange = useCallback((value: number): void => {
		const newValues = { front: value };
		setThicknessValues(newValues);
		applyConfig(true, newValues);
	}, [applyConfig]);

	const handlePanelThicknessChange = useCallback((role: ExtendedPanelRole, value: number): void => {
		setThicknessValues((prev) => {
			const newValues = { ...prev, [role]: value };
			applyConfig(false, newValues);
			return newValues;
		});
	}, [applyConfig]);

	const handleUniformToggle = useCallback((checked: boolean): void => {
		setUniform(checked);
		if (checked && island) {
			const frontThickness = island.getThicknessForRole('front');
			const newValues = { front: frontThickness };
			setThicknessValues(newValues);
			applyConfig(true, newValues);
		} else if (island) {
			const newValues: PanelThicknessOverrides = {
				front: island.getThicknessForRole('front'),
				left: island.getThicknessForRole('left'),
				right: island.getThicknessForRole('right')
			};
			if (island.isCornerIsland) {
				newValues.backLeft = island.getThicknessForRole('backLeft');
				newValues.backRight = island.getThicknessForRole('backRight');
			} else {
				newValues.back = island.getThicknessForRole('back');
			}
			setThicknessValues(newValues);
			applyConfig(false, newValues);
		}
	}, [island, applyConfig]);

	const handleResetToDefault = useCallback((): void => {
		if (!island) return;
		island.panelThicknessConfig = null;
		setAdvancedOpen(false);
		setUniform(true);
		const defaultThickness = island.surround?.getThicknessForRole('front') ?? 0.75;
		setThicknessValues({ front: defaultThickness });
	}, [island]);

	return {
		advancedOpen,
		uniform,
		thicknessValues,
		setAdvancedOpen,
		initFromIsland,
		handleUniformToggle,
		handleUniformThicknessChange,
		handlePanelThicknessChange,
		handleResetToDefault
	};
};
